import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getProduct, getFlavors } from '../services/api';
import { products as mockProducts, flavors as mockFlavors } from '../data/mock';
import SEO from '../components/SEO';
import { useCart } from '../context/CartContext';
import { ArrowLeft, MapPin, Check, ShoppingCart } from 'lucide-react';

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [allFlavors, setAllFlavors] = useState(mockFlavors);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);
  const { addToCart } = useCart();

  useEffect(() => {
    Promise.all([getProduct(id), getFlavors()])
      .then(([prod, flavs]) => {
        setProduct(prod);
        setAllFlavors(flavs);
      })
      .catch((err) => {
        if (err?.response?.status === 404) {
          const mock = mockProducts.find((p) => p.id === id);
          if (mock) setProduct(mock);
          else setNotFound(true);
        } else {
          const mock = mockProducts.find((p) => p.id === id);
          if (mock) setProduct(mock);
          else setNotFound(true);
        }
        setAllFlavors(mockFlavors);
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <main className="min-h-screen pt-24 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#0a0a0a] border-t-transparent" />
      </main>
    );
  }

  if (notFound || !product) {
    return (
      <main className="min-h-screen pt-24 flex items-center justify-center">
        <div className="text-center">
          <h1
            className="text-[#0a0a0a] font-black italic text-4xl mb-4"
            style={{ fontFamily: 'Nunito, sans-serif' }}
          >
            Product not found
          </h1>
          <Link
            to="/shop"
            className="text-[#0a0a0a] font-bold underline"
            style={{ fontFamily: 'Nunito, sans-serif' }}
          >
            Back to shop
          </Link>
        </div>
      </main>
    );
  }

  const productFlavors = (product.flavors || [])
    .map((fid) => allFlavors.find((f) => f.id === fid))
    .filter(Boolean);

  return (
    <main className="min-h-screen bg-white pt-20">
      <SEO
        title={`${product.name} | Seltz`}
        url={`https://seltz.eu/products/${product.id}`}
        description={product.longDescription}
        image={product.image}
      />
      {/* Hero */}
      <section
        className="relative py-20 overflow-hidden"
        style={{ background: 'linear-gradient(145deg, #0f0f0f 0%, #0a0a0a 100%)' }}
      >
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <Link
            to="/shop"
            className="inline-flex items-center gap-2 text-white/70 hover:text-white font-bold text-sm mb-8 transition-colors"
            style={{ fontFamily: 'Nunito, sans-serif' }}
          >
            <ArrowLeft size={16} />
            înapoi la magazin
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Product Image */}
            <div className="flex justify-center">
              <div className="relative">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full max-w-sm h-auto object-contain drop-shadow-2xl"
                  style={{ filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.4))' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                {product.badge && (
                  <span
                    className="absolute top-4 right-4 bg-white text-[#0a0a0a] font-black text-sm px-4 py-2 rounded-full shadow-lg"
                    style={{ fontFamily: 'Nunito, sans-serif' }}
                  >
                    {product.badge}
                  </span>
                )}
              </div>
            </div>

            {/* Product Info */}
            <div className="flex flex-col gap-6">
              <div>
                <h1
                  className="text-white font-black italic leading-tight mb-3"
                  style={{
                    fontFamily: 'Nunito, sans-serif',
                    fontSize: 'clamp(2.5rem, 5vw, 4rem)',
                  }}
                >
                  {product.name}
                </h1>
                <p className="text-white/80 text-base leading-relaxed" style={{ fontFamily: 'Nunito, sans-serif' }}>
                  {product.longDescription}
                </p>
              </div>

              {/* Price & specs */}
              <div className="flex items-center gap-4">
                <span
                  className="text-white font-black text-3xl"
                  style={{ fontFamily: 'Nunito, sans-serif' }}
                >
                  ${product.price}
                </span>
                <span
                  className="text-white/60 text-sm"
                  style={{ fontFamily: 'Nunito, sans-serif' }}
                >
                  {product.cans} doze • {product.specs}
                </span>
              </div>

              {/* Tags */}
              <div className="flex gap-2 flex-wrap">
                {(product.tags || []).map((tag) => (
                  <span
                    key={tag}
                    className="bg-white/20 text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1"
                    style={{ fontFamily: 'Nunito, sans-serif' }}
                  >
                    <Check size={12} />
                    {tag}
                  </span>
                ))}
              </div>

              {/* CTA Buttons */}
              <div className="flex gap-4">
                <button
                  className={`flex-1 flex items-center justify-center gap-2 font-black italic text-base py-4 rounded-2xl transition-all duration-200 hover:scale-105 shadow-lg ${addedToCart ? 'bg-green-500 text-white' : 'bg-white text-[#0a0a0a] hover:bg-white/90'
                    }`}
                  style={{ fontFamily: 'Nunito, sans-serif' }}
                  onClick={() => {
                    addToCart(product);
                    setAddedToCart(true);
                    setTimeout(() => setAddedToCart(false), 2000);
                  }}
                >
                  {addedToCart ? <><Check size={18} /> adăugat!</> : <><ShoppingCart size={18} /> adaugă în coș</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Flavors in this pack */}
      {productFlavors.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <h2
              className="text-[#0a0a0a] font-black italic text-3xl text-center mb-10"
              style={{ fontFamily: 'Nunito, sans-serif' }}
            >
              ce conține
            </h2>
            <div className="flex flex-wrap justify-center gap-6 md:gap-8 lg:gap-10">
              {productFlavors.map((flavor) => (
                <div key={flavor.id} className="flex flex-col items-center gap-3 group w-full sm:w-[260px]">
                  <div
                    className="w-full rounded-3xl overflow-hidden flex items-center justify-center p-4 transition-all duration-300 group-hover:shadow-xl"
                    style={{
                      background: 'linear-gradient(160deg, #1a1a1a 0%, #0a0a0a 60%, #111111 100%)',
                      height: '220px',
                    }}
                  >
                    <img
                      src={flavor.image}
                      alt={flavor.name}
                      className="h-36 w-auto object-contain transition-transform duration-300 group-hover:scale-110"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                  <span
                    className="px-5 py-2 rounded-2xl font-black italic text-sm"
                    style={{
                      fontFamily: 'Nunito, sans-serif',
                      backgroundColor: flavor.color,
                      color: flavor.textColor,
                    }}
                  >
                    {flavor.name}
                  </span>
                  <p className="text-gray-500 text-xs text-center" style={{ fontFamily: 'Nunito, sans-serif' }}>
                    {flavor.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Other products */}
      <section className="py-16" style={{ background: '#f8f9ff' }}>
        <div className="max-w-6xl mx-auto px-6">
          <h2
            className="text-[#0a0a0a] font-black italic text-3xl text-center mb-10"
            style={{ fontFamily: 'Nunito, sans-serif' }}
          >
            alte opțiuni
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {mockProducts.map((p) => (
              <Link key={p.id} to={`/products/${p.id}`} className="group flex flex-col gap-3">
                <div
                  className="rounded-2xl overflow-hidden flex items-center justify-center p-6 transition-all duration-300 group-hover:shadow-xl"
                  style={{
                    background: 'linear-gradient(160deg, #1a1a1a 0%, #0a0a0a 60%, #111111 100%)',
                    height: '240px',
                  }}
                >
                  <img
                    src={p.image}
                    alt={p.name}
                    className="h-36 w-auto object-contain transition-transform duration-300 group-hover:scale-110"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
                <h3
                  className="text-[#0a0a0a] font-black italic text-lg text-center"
                  style={{ fontFamily: 'Nunito, sans-serif' }}
                >
                  {p.name}
                </h3>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default ProductDetail;
